/**
 * Timezone detection and caching utilities
 * Implements smart timezone detection with localStorage persistence
 */

const TIMEZONE_STORAGE_KEY = 'altitude_user_timezone';

/**
 * Detects user's timezone using browser API with fallback to UTC
 */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Failed to detect timezone, falling back to UTC:', error);
    return 'UTC';
  }
}

/**
 * Gets cached timezone from localStorage or detects and caches it
 */
export function getUserTimezone(): string {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || !window.localStorage) {
    return 'UTC';
  }
  
  try {
    // Try to get cached timezone
    const cachedTimezone = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (cachedTimezone && isValidTimezone(cachedTimezone)) {
      return cachedTimezone;
    }
  } catch (error) {
    console.warn('Failed to read timezone from localStorage:', error);
  }

  // Detect and cache new timezone
  const detectedTimezone = detectTimezone();
  setUserTimezone(detectedTimezone);
  return detectedTimezone;
}

/**
 * Sets and caches user timezone in localStorage
 */
export function setUserTimezone(timezone: string): void {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  
  if (!isValidTimezone(timezone)) {
    console.warn('Invalid timezone provided, falling back to UTC:', timezone);
    timezone = 'UTC';
  }

  try {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
  } catch (error) {
    console.warn('Failed to cache timezone in localStorage:', error);
  }
}

/**
 * Validates timezone against browser support
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    // Try to create a date formatter with the timezone
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Gets user's local date string in YYYY-MM-DD format
 * Uses cached timezone for consistency
 */
export function getUserLocalDate(date: Date = new Date()): string {
  const timezone = getUserTimezone();
  try {
    return new Intl.DateTimeFormat('en-CA', { 
      timeZone: timezone 
    }).format(date);
  } catch (error) {
    console.warn('Failed to format date with timezone, falling back to UTC:', error);
    return new Intl.DateTimeFormat('en-CA', { 
      timeZone: 'UTC' 
    }).format(date);
  }
}

/**
 * Clear cached timezone (useful for testing or manual timezone changes)
 */
export function clearCachedTimezone(): void {
  try {
    localStorage.removeItem(TIMEZONE_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear cached timezone:', error);
  }
}